import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Loader2, Sparkles, ArrowUpRight, ArrowDownRight, Minus, CheckCircle2, XCircle, Eye } from "lucide-react";
import { currency } from "@/lib/constants";

const REC_META = {
  buy: { label: "BUY", color: "#34d399", Icon: CheckCircle2, bg: "rgba(52,211,153,0.08)" },
  watch: { label: "WATCH", color: "#fbbf24", Icon: Eye, bg: "rgba(251,191,36,0.08)" },
  skip: { label: "SKIP", color: "#f87171", Icon: XCircle, bg: "rgba(248,113,113,0.08)" },
};

const Metric = ({ label, value, unit = "", trend, sub, color = "#f4ede2" }) => (
  <div className="border border-[#33291f] rounded p-3 bg-[#14100c]">
    <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] font-semibold flex items-center gap-1">
      {label}
      {trend === "up" && <ArrowUpRight size={10} className="text-[#34d399]" />}
      {trend === "down" && <ArrowDownRight size={10} className="text-[#f87171]" />}
      {trend === "flat" && <Minus size={10} className="text-[#7a6d5a]" />}
    </div>
    <div className="font-mono text-[18px] font-semibold mt-1" style={{ color }}>{value}<span className="text-[11px] text-[#7a6d5a] ml-0.5">{unit}</span></div>
    {sub && <div className="text-[10px] text-[#7a6d5a] mt-0.5">{sub}</div>}
  </div>
);

export default function InvestmentAnalysisCard({ opp, onUpdated }) {
  const [analysis, setAnalysis] = useState(opp.investment_analysis || null);
  const [running, setRunning] = useState(false);

  const analyze = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/ai/investment-analysis/${opp.id}`);
      setAnalysis(r.data.investment_analysis);
      toast.success("AI investment analysis complete.");
      onUpdated?.(r.data.investment_analysis);
    } catch (e) { toast.error("Analysis failed. Try again."); } finally { setRunning(false); }
  };

  if (!analysis) {
    return (
      <div className="border border-[#33291f] rounded-lg bg-gradient-to-br from-[#241d16] to-[#1c1712] p-6" data-testid="investment-analysis-empty">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp size={16} className="text-[#d4a574]" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] font-semibold">AI Investment Analysis</div>
        </div>
        <div className="font-display text-lg font-semibold mb-2">Underwrite this opportunity in seconds.</div>
        <p className="text-[13px] text-[#b8ac96] mb-4 leading-relaxed">
          Get ROI projections, cap rate, cash-on-cash, break-even timeline, thesis, key risks, and a
          buy/watch/skip recommendation — powered by Claude Sonnet.
        </p>
        <button onClick={analyze} disabled={running} data-testid="run-investment-analysis" className="flex items-center gap-2 px-4 py-2.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px] hover:bg-[#e8b988] disabled:opacity-60">
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "Underwriting…" : "Run AI Investment Analysis"}
        </button>
      </div>
    );
  }

  const rec = REC_META[analysis.recommendation] || REC_META.watch;

  return (
    <div className="border border-[#33291f] rounded-lg bg-gradient-to-br from-[#241d16] to-[#1c1712] p-5 space-y-4" data-testid="investment-analysis-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-[#d4a574]" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] font-semibold">AI Investment Analysis</div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-4xl font-bold" style={{ color: rec.color }}>{Math.round(analysis.investment_score)}</span>
            <span className="text-[11px] text-[#7a6d5a] font-mono">/ 100 investment score</span>
          </div>
        </div>
        <div className="text-right">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border font-semibold text-[13px] tracking-wider" style={{ color: rec.color, borderColor: rec.color, background: rec.bg }} data-testid={`rec-${analysis.recommendation}`}>
            <rec.Icon size={14} /> {rec.label}
          </div>
          <div className="text-[10px] text-[#7a6d5a] uppercase tracking-wider mt-1.5 font-mono">{analysis.conviction} conviction</div>
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Metric label="1yr ROI" value={analysis.roi_1yr_pct?.toFixed(1)} unit="%" color="#34d399" trend="up" />
        <Metric label="5yr ROI" value={analysis.roi_5yr_pct?.toFixed(1)} unit="%" color="#34d399" trend="up" />
        <Metric label="Cap Rate" value={analysis.cap_rate_pct?.toFixed(1)} unit="%" color="#d4a574" />
        <Metric label="Cash-on-Cash" value={analysis.cash_on_cash_pct?.toFixed(1)} unit="%" color="#d4a574" />
        <Metric label="Break Even" value={analysis.break_even_months} unit="mo" />
        <Metric label="Hold Period" value={analysis.hold_period_years} unit="yr" />
        <Metric label="Repair Cost" value={currency(analysis.estimated_repair_cost || 0)} color="#f87171" />
        <Metric label="ARV" value={currency(analysis.estimated_arv || 0)} color="#34d399" trend="up" />
      </div>

      {/* Thesis */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#d4a574] font-semibold mb-1.5">Investment Thesis</div>
        <div className="text-[13px] text-[#f4ede2] leading-relaxed border-l-2 border-[#d4a574] pl-3 italic">
          "{analysis.thesis}"
        </div>
      </div>

      {/* Upsides / Risks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#34d399] font-semibold mb-1.5">Key Upsides</div>
          <ul className="space-y-1">
            {(analysis.key_upsides || []).map((u, i) => (
              <li key={i} className="text-[12.5px] text-[#b8ac96] flex items-start gap-1.5">
                <ArrowUpRight size={11} className="text-[#34d399] mt-0.5 shrink-0" /> {u}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#f87171] font-semibold mb-1.5">Key Risks</div>
          <ul className="space-y-1">
            {(analysis.key_risks || []).map((r, i) => (
              <li key={i} className="text-[12.5px] text-[#b8ac96] flex items-start gap-1.5">
                <ArrowDownRight size={11} className="text-[#f87171] mt-0.5 shrink-0" /> {r}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Exit + comps */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-[#33291f]">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Exit Strategy</div>
          <div className="text-[12.5px] text-[#f4ede2] mt-0.5">{analysis.exit_strategy}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Comparables</div>
          <div className="text-[12.5px] text-[#f4ede2] mt-0.5">{analysis.comparable_deals}</div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="text-[9px] text-[#7a6d5a] uppercase tracking-wider font-mono">AI-Generated · Not investment advice</div>
        <button onClick={analyze} disabled={running} data-testid="rerun-investment-analysis" className="text-[11px] text-[#d4a574] hover:text-[#e8b988] flex items-center gap-1">
          {running ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Re-analyze
        </button>
      </div>
    </div>
  );
}
